import { TablePaginationConfig } from "antd";
import { activityLogApi, activityLogStatisticsApi, exportActivityLogsApi, IActivityLogStats } from "api/activityLog";
import { useNotificationContext } from "contexts/NotificationContext";
import { defaultTableData, ITableData } from "models";
import { IActivityLog } from "models/activityLog";
import { useEffect, useState, useCallback } from "react";

export default function useActivityLog() {
  const { showError } = useNotificationContext();
  const [data, setData] = useState<ITableData<IActivityLog>>(defaultTableData);
  const [stats, setStats] = useState<IActivityLogStats | null>(null);
  const [currentFilters, setCurrentFilters] = useState<any>({});

  const fetchActivityLog = useCallback(async (filters: any = {}) => {
    setData((prev) => ({ ...prev, loading: true }));
    // Persist filters across page changes
    const mergedFilters = { ...currentFilters, ...filters };
    setCurrentFilters(mergedFilters);

    const { data: response, error } = await activityLogApi({
      page: data.page,
      per_page: data.per_page,
      search: data.search,
      ...mergedFilters,
    });
    if (response) {
      response.data = response.data.map((value) => ({
        ...value,
        key: value.id,
      }));
      setData((prev) => ({ ...prev, ...response, loading: false }));
    } else {
      showError(error?.message || "Failed to fetch logs");
      setData((prev) => ({ ...prev, loading: false }));
    }
  }, [data.page, data.per_page, data.search, currentFilters, showError]);

  const fetchStatistics = async () => {
    try {
      const { data: response, error } = await activityLogStatisticsApi();
      if (response && response.data) {
        setStats(response.data);
      } else {
        showError(error?.message || "Failed to fetch statistics");
      }
    } catch (err) {
      showError("Failed to fetch statistics");
    }
  };

  useEffect(() => {
    fetchActivityLog();
    fetchStatistics();
  }, [data.page, data.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = ({ current, pageSize }: TablePaginationConfig) => {
    if (current) setData((prev) => ({ ...prev, page: current, per_page: pageSize ?? prev.per_page }));
  };

  const handleSearch = (value: string) => {
    setData((prev) => ({ ...prev, search: value, page: 1 }));
  };

  const exportLogs = async (format: "csv" | "excel") => {
    try {
      const { data: response, error } = await exportActivityLogsApi({
        format,
        ...currentFilters,
        search: data.search
      });
      if (response?.download_url) {
        window.open(response.download_url, '_blank');
      } else {
        showError(error?.message || "Export failed");
      }
    } catch (err) {
      showError("Failed to export logs");
    }
  };

  return {
    data,
    stats,
    handlePageChange,
    handleSearch,
    fetchActivityLog,
    fetchStatistics,
    exportLogs,
  };
}
